import { useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/ar';
import useGlobalVariable from '@lib/store/hooks/useGlobalVariables';
import styles from './event.module.css';

const formatDateParts = (dateString = '', fallbackWeekday = '', locale = 'en') => {
  const fallback = {
    day: dateString,
    weekday: fallbackWeekday || '',
    monthName: '',
    ordinalDay: '',
    label: dateString,
  };

  if (!dateString) return fallback;

  const clean = dateString.replace(/\s+/g, '');
  const parts = clean.split(/[./-]/);
  if (parts.length < 3) return fallback;

  const [dayRaw, monthRaw, yearRaw] = parts;
  const day = dayRaw.padStart(2, '0');
  const month = monthRaw.padStart(2, '0');
  const year = yearRaw.length === 2 ? `20${yearRaw}` : yearRaw;

  const parsed = dayjs(`${year}-${month}-${day}`);
  if (!parsed.isValid()) return fallback;

  const localized = parsed.locale(locale || 'en');
  const weekday = localized.format('dddd') || fallbackWeekday || '';

  return {
    day: localized.format('DD'),
    weekday,
    monthName: localized.format('MMMM'),
    ordinalDay: localized.format('D'),
    label: localized.format('D MMMM YYYY'),
  };
};

export default function HolidaysCalendarEvent({ eventData, locale }) {
  const [open, setOpen] = useState(false);
  const { gv } = useGlobalVariable();
  const dateParts = formatDateParts(eventData?.date, eventData?.dayOfTheWeek, locale);
  const tableRows = eventData?.tableRows ?? [];

  return (
    <div className={styles.event}>
      <button
        type="button"
        aria-expanded={open}
        className={styles.eventButton}
        onClick={() => setOpen((s) => !s)}
      >
        <div className={styles.eventSummary}>
          <div className={styles.dateCard}>
            <span className={styles.dateDay}>{dateParts.day}</span>
            <span className={styles.dateWeekday}>{dateParts.weekday}</span>
          </div>
          <div className={styles.eventInfo}>
            <span className={styles.eventName}>{eventData?.name}</span>
            <span className={styles.eventDate}>{dateParts.label}</span>
          </div>
        </div>
        <span className={`${styles.arrow} ${open ? styles.arrowOpen : ''}`} aria-hidden>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6 9 12 15 18 9" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>

      {open && (
        <div className={styles.panel}>
          <div className={styles.table}>
            <div className={`${styles.tableRow} ${styles.tableHeaderRow}`}>
              <span>{gv('tc_asset_class', 'raw')}</span>
              <span>{gv('tc_symbols', 'raw')}</span>
              <span>{gv('tc_trading_hour', 'raw')}</span>
            </div>
            {tableRows.map((row, idx) => {
              const symbols = row?.symbols ?? [];
              const hours = row?.tradinghours ?? [];
              return (
                <div key={`${row?.name || 'asset'}-${idx}`} className={styles.tableRow}>
                  <div className={`${styles.cell} ${styles.cellTitle}`}>{row?.name || gv('tc_asset', 'raw')}</div>
                  <div className={styles.cell}>
                    {symbols.length ? (
                      <div className={styles.symbols}>
                        {symbols.map((sym, sIdx) => (
                          <span key={`${sym?.symbol || 'symbol'}-${sIdx}`} className={styles.symbolPill}>
                            {sym?.symbol || sym}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className={styles.placeholder}>-</span>
                    )}
                  </div>
                  <div className={styles.cell}>
                    {hours.length ? (
                      <div className={styles.hoursList}>
                        {hours.map((h, hIdx) => (
                          <span key={`${h?.state || 'state'}-${hIdx}`} className={styles.statusChip}>
                            {h?.state || h}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className={styles.placeholder}>-</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
